/**
 * API Service for communicating with FastAPI backend
 */
class ApiService {
  constructor() {
    // Prefer environment-configured base URL; fallback smartly per environment
    const isBrowser = typeof window !== 'undefined';
    const isProdBuild = !(typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production');
    const devBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE_URL_DEV) ? process.env.REACT_APP_API_BASE_URL_DEV : null;
    const prodBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE_URL) ? process.env.REACT_APP_API_BASE_URL : null;
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('api-base-url-changed', { detail: { baseUrl: this.baseUrl } }));
        }
      } catch {}

    // Order of preference:
    // 1) Explicit prod env var
    // 2) Explicit dev env var
    // 3) If running on the Fly frontend domain, hardcode backend domain
    // 4) Same-origin in browser (useful for local proxy setups)
    // 5) Local backend default
    let rawBase = prodBase || devBase || null;
    if (!rawBase) {
      if (isBrowser) {
        const origin = window.location?.origin || '';
        const host = window.location?.host || '';
        if (/(^|\.)lodia-frontend\.fly\.dev$/i.test(host) || /(app\.lodia\.se|\.lodia\.se)$/i.test(host)) {
          rawBase = 'https://lodia-backend.fly.dev';
        } else if (/^(localhost|127\.0\.0\.1)(:3000)?$/i.test(host)) {
          // Local dev: prefer FastAPI (default 8000) unless explicitly overridden.
          // If you still run the old Node backend on 3001 set REACT_APP_API_BASE_URL_DEV.
          rawBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE_URL_DEV)
            ? process.env.REACT_APP_API_BASE_URL_DEV.replace(/\/$/, '')
            : 'http://localhost:8000';
        } else {
          rawBase = origin; // same-origin fallback
        }
      } else {
        rawBase = 'http://127.0.0.1:8000';
      }
    }

    // Check for persisted override from prior fallback (hot reload safety)
    let persisted = null;
    if (isBrowser) {
      try { persisted = localStorage.getItem('api_base_override'); } catch {}
    }
    if (persisted) {
      rawBase = persisted;
    }

    const trimmed = (rawBase || '').replace(/\/$/, '');
    this.baseUrl = `${trimmed}/api`;

    // Proactive local dev probe: if on localhost defaulting to 8000, test it; if unreachable, switch to 3001 immediately.
    if (isBrowser && /localhost|127\.0\.0\.1/.test(trimmed) && /:8000$/.test(trimmed)) {
      this._probeLocalFallback(trimmed).catch(() => {});
    }
  }

  /**
   * Set the auth context for making authenticated requests
   * @param {Object} authContext - The auth context from useAuth hook
   */
  setAuthContext(authContext) {
    this.authContext = authContext;
  }

  async _probeLocalFallback(originalBase) {
    if (this._probed) return; this._probed = true;
    const healthUrl = `${originalBase}/api/health`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 1200);
    let ok = false;
    try {
      const res = await fetch(healthUrl, { signal: controller.signal });
      ok = res.ok;
    } catch (_) { ok = false; }
    clearTimeout(timer);
    if (!ok) {
      const alt = originalBase.replace(':8000', ':3001');
      console.warn('[ApiService] probe: :8000 unreachable, switching base to', alt);
  this.baseUrl = `${alt}/api`;
  try { if (typeof window !== 'undefined') localStorage.setItem('api_base_override', alt); } catch {}
      try { window.dispatchEvent(new CustomEvent('api-base-url-changed', { detail: { baseUrl: this.baseUrl, reason: 'probe-fallback' } })); } catch {}
    }
  }

  /**
   * Make an authenticated request to the backend
   * @param {string} endpoint - API endpoint (without /api prefix)
   * @param {Object} options - Fetch options
   * @returns {Promise<Response>}
   */
  async request(endpoint, options = {}) {
    if (!this.authContext) {
      throw new Error('Auth context not set. Call setAuthContext first.');
    }

  // Ensure endpoint begins with '/'
  const safeEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  try {
    return await this.authContext.authenticatedFetch(`${this.baseUrl}${safeEndpoint}`, options);
  } catch (e) {
    // Network-level error (connection refused) → attempt single fallback from 8000 → 3001
    const msg = (e && e.message) || '';
    if (!this._fallbackTried && /8000/.test(this.baseUrl) && (msg.includes('Failed to fetch') || msg.includes('Network') || msg.includes('refused'))) {
      this._fallbackTried = true;
      const alt = this.baseUrl.replace(':8000', ':3001');
      console.warn('[ApiService] primary backend unreachable, trying fallback', { from: this.baseUrl, to: alt });
      this.baseUrl = alt; // swap
      return await this.authContext.authenticatedFetch(`${this.baseUrl}${safeEndpoint}`, options);
    }
    throw e;
  }
  }

  // ==================== USER ENDPOINTS ====================
  
  /**
   * Get current user profile
   * @returns {Promise<Object>}
   */
  async getCurrentUser() {
    const response = await this.request('/users/me');
    return await response.json();
  }

  /**
   * Update user profile
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>}
   */
  async updateUser(userData) {
    const response = await this.request('/users/me', {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
    return await response.json();
  }

  // ==================== FEEDBACK ====================
  async submitFeedback(payload) {
    const response = await this.request('/feedback', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      console.warn('submitFeedback non-JSON response', { status: response.status, text: text.slice(0,300) });
      throw new Error(`Submit failed (status ${response.status})`);
    }
  }
  async listFeedback({ limit = 50, cursor } = {}) {
    const qs = new URLSearchParams();
    if (limit) qs.set('limit', String(limit));
    if (cursor) qs.set('cursor', cursor);
    const response = await this.request(`/feedback?${qs.toString()}`);
    const raw = await response.text();
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.warn('listFeedback non-JSON response', { status: response.status, raw: raw.slice(0,300) });
      throw new Error(`Feedback fetch failed (status ${response.status})`);
    }
  }

  // ==================== LINES ENDPOINTS ====================
  
  /**
   * Get user's lines
   * @returns {Promise<Array>}
   */
  async getLines() {
    const response = await this.request('/lines');
    return await response.json();
  }

  /**
   * Create a new line
   * @param {Object} lineData - Line data
   * @returns {Promise<Object>}
   */
  async createLine(lineData) {
    const response = await this.request('/lines', {
      method: 'POST',
      body: JSON.stringify(lineData)
    });
    return await response.json();
  }

  /**
   * Update a line
   * @param {string} lineId - Line ID
   * @param {Object} lineData - Updated line data
   * @returns {Promise<Object>}
   */
  async updateLine(lineId, lineData) {
    const response = await this.request(`/lines/${lineId}`, {
      method: 'PUT',
      body: JSON.stringify(lineData)
    });
    return await response.json();
  }

  /**
   * Delete a line
   * @param {string} lineId - Line ID
   * @returns {Promise<void>}
   */
  async deleteLine(lineId) {
    await this.request(`/lines/${lineId}`, {
      method: 'DELETE'
    });
  }

  // ==================== TRUCKS ENDPOINTS ====================
  
  /**
   * Get trucks for a line
   * @param {string} lineId - Line ID
   * @returns {Promise<Array>}
   */
  async getTrucks(lineId) {
    const response = await this.request(`/lines/${lineId}/trucks`);
    return await response.json();
  }

  /**
   * Create a new truck
   * @param {string} lineId - Line ID
   * @param {Object} truckData - Truck data
   * @returns {Promise<Object>}
   */
  async createTruck(lineId, truckData) {
    const response = await this.request(`/lines/${lineId}/trucks`, {
      method: 'POST',
      body: JSON.stringify(truckData)
    });
    return await response.json();
  }

  /**
   * Update a truck
   * @param {string} lineId - Line ID
   * @param {string} truckId - Truck ID
   * @param {Object} truckData - Updated truck data
   * @returns {Promise<Object>}
   */
  async updateTruck(lineId, truckId, truckData) {
    const response = await this.request(`/lines/${lineId}/trucks/${truckId}`, {
      method: 'PUT',
      body: JSON.stringify(truckData)
    });
    return await response.json();
  }

  /**
   * Delete a truck
   * @param {string} lineId - Line ID
   * @param {string} truckId - Truck ID
   * @returns {Promise<void>}
   */
  async deleteTruck(lineId, truckId) {
    await this.request(`/lines/${lineId}/trucks/${truckId}`, {
      method: 'DELETE'
    });
  }

  // ==================== PALLETS ENDPOINTS ====================
  
  /**
   * Get pallets for a truck
   * @param {string} lineId - Line ID
   * @param {string} truckId - Truck ID
   * @returns {Promise<Array>}
   */
  async getPallets(lineId, truckId) {
    const response = await this.request(`/lines/${lineId}/trucks/${truckId}/pallets`);
    return await response.json();
  }

  /**
   * Create a new pallet
   * @param {string} lineId - Line ID
   * @param {string} truckId - Truck ID
   * @param {Object} palletData - Pallet data
   * @returns {Promise<Object>}
   */
  async createPallet(lineId, truckId, palletData) {
    const response = await this.request(`/lines/${lineId}/trucks/${truckId}/pallets`, {
      method: 'POST',
      body: JSON.stringify(palletData)
    });
    return await response.json();
  }

  /**
   * Update a pallet
   * @param {string} lineId - Line ID
   * @param {string} truckId - Truck ID
   * @param {string} palletId - Pallet ID
   * @param {Object} palletData - Updated pallet data
   * @returns {Promise<Object>}
   */
  async updatePallet(lineId, truckId, palletId, palletData) {
    const response = await this.request(`/lines/${lineId}/trucks/${truckId}/pallets/${palletId}`, {
      method: 'PUT',
      body: JSON.stringify(palletData)
    });
    return await response.json();
  }

  /**
   * Delete a pallet
   * @param {string} lineId - Line ID
   * @param {string} truckId - Truck ID
   * @param {string} palletId - Pallet ID
   * @returns {Promise<void>}
   */
  async deletePallet(lineId, truckId, palletId) {
    await this.request(`/lines/${lineId}/trucks/${truckId}/pallets/${palletId}`, {
      method: 'DELETE'
    });
  }

  // ==================== SOLVER ENDPOINTS ====================
  
  /**
   * Run the solver for a truck
   * @param {string} lineId - Line ID
   * @param {string} truckId - Truck ID
   * @param {Object} solverOptions - Solver configuration
   * @returns {Promise<Object>}
   */
  async runSolver(lineId, truckId, solverOptions = {}) {
    const response = await this.request(`/arrange`, {
      method: 'POST',
      body: JSON.stringify({
        line_id: lineId,
        truck_id: truckId,
        ...solverOptions
      })
    });
    return await response.json();
  }

  /**
   * Run the greedy solver variant
   */
  async runSolverGreedy(lineId, truckId, solverOptions = {}) {
    const response = await this.request(`/arrange-greedy`, {
      method: 'POST',
      body: JSON.stringify({
        line_id: lineId,
        truck_id: truckId,
        ...solverOptions
      })
    });
    return await response.json();
  }

  /**
   * Get solver results
   * @param {string} resultId - Solver result ID
   * @returns {Promise<Object>}
   */
  async getSolverResult(resultId) {
    // Not implemented on backend; placeholder for future
    throw new Error('Not implemented');
  }

  // ==================== SAVED LOADS ENDPOINTS ====================
  
  /**
   * Get saved loads (optionally by date range)
   * @param {{ from?: string, to?: string }} [range]
   * @returns {Promise<Array>}
   */
  async getSavedLoads(range) {
    const params = new URLSearchParams();
    if (range?.from) params.set('from', range.from);
    if (range?.to) params.set('to', range.to);
    const qs = params.toString();
    const endpoint = qs ? `/loads?${qs}` : '/loads';
    const response = await this.request(endpoint);
    return await response.json();
  }

  /**
   * Save a load configuration
   * @param {Object} loadData - Load data with manifest info
   * @returns {Promise<Object>}
   */
  async saveLoad(loadData) {
    // loadData may include: { date?: 'YYYY-MM-DD', name, description, line_id, truck_id, pallets_data, row_weights, truck_data, line_data }
    const response = await this.request('/loads', {
      method: 'POST',
      body: JSON.stringify(loadData)
    });
    return await response.json();
  }

  /**
   * Update a saved load
   * @param {string} loadId - Load ID
   * @param {Object} loadData - Updated load data
   * @returns {Promise<Object>}
   */
  async updateLoad(loadId, loadData) {
    const response = await this.request(`/loads/${loadId}`, {
      method: 'PUT',
      body: JSON.stringify(loadData)
    });
    return await response.json();
  }

  /**
   * Delete a saved load
   * @param {string} loadId - Load ID
   * @returns {Promise<void>}
   */
  async deleteLoad(loadId) {
    await this.request(`/loads/${loadId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Update a saved load
   * @param {string|number} loadId
   * @param {Object} patch - accepts fields including date, name, description, pallets_data, row_weights, etc.
   */
  async patchLoad(loadId, patch) {
    const response = await this.request(`/loads/${loadId}`, {
      method: 'PUT',
      body: JSON.stringify(patch || {})
    });
    return await response.json();
  }

  // ==================== STATS ENDPOINTS ====================
  /**
   * Get daily stats within a date range
   * @param {{ from?: string, to?: string }} [range]
   */
  async getDailyStats(range) {
    const params = new URLSearchParams();
    if (range?.from) params.set('from', range.from);
    if (range?.to) params.set('to', range.to);
    const qs = params.toString();
    const endpoint = qs ? `/stats/daily?${qs}` : '/stats/daily';
    const response = await this.request(endpoint);
    return await response.json();
  }

  // ==================== AUTOSAVE ENDPOINTS ====================
  
  /**
   * Save autosave payload (full app state snapshot)
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async saveAutosave(data) {
    const response = await this.request('/autosave', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return await response.json();
  }

  /**
   * Get autosave payload
   * @returns {Promise<Object|null>}
   */
  async getAutosave() {
    const response = await this.request('/autosave');
    if (response.status === 404) return null;
    return await response.json();
  }

  // ==================== ROW WEIGHTS ENDPOINTS ====================
  
  /**
   * Calculate row weights for a truck
   * @param {string} lineId - Line ID
   * @param {string} truckId - Truck ID
   * @returns {Promise<Object>}
   */
  async calculateRowWeights(payload) {
    // payload: { pallets: [], truckDimensions: { depth }, rows }
    const response = await this.request('/calculate-row-weights', {
      method: 'POST',
      body: JSON.stringify(payload || {})
    });
    return await response.json();
  }
}

export const apiService = new ApiService();
export default apiService;
